import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Button, TextField, Typography, Paper, Grid, Alert, MenuItem, Select,
  FormControl, InputLabel, SelectChangeEvent, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { PlayArrow, VolumeUp, CloudUpload, Delete, MyLocation, WarningAmber } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../api/client';
import { playSound } from '../../utils/sounds';
import RichTextEditor from '../../components/RichTextEditor';

const TIMEZONES = [
  'UTC', 'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
  'US/Alaska', 'US/Hawaii', 'America/Chicago', 'America/New_York',
  'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
  'Europe/London', 'Europe/Berlin', 'Asia/Tokyo',
];

const SOUND_OPTIONS = ['tone_alert', 'chime', 'dispatch_alert', 'notification', 'urgent', 'two_tone', 'bell', 'soft_chime', 'ding_dong', 'triple_chime', 'bubble', 'glass', 'marimba', 'ping', 'none'];

function SoundSelect({ label, value, onChange, field }: {
  label: string; value: string; onChange: (field: string) => (e: SelectChangeEvent<string>) => void; field: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl fullWidth margin="dense">
        <InputLabel>{label}</InputLabel>
        <Select value={value} label={label} onChange={onChange(field)}>
          {SOUND_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
      </FormControl>
      <Tooltip title={`Preview: ${value}`}>
        <IconButton
          color="primary"
          onClick={() => playSound(value)}
          sx={{ mt: 0.5 }}
          size="small"
        >
          <PlayArrow />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function SystemConfigPage() {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerRef = useRef<L.CircleMarker | null>(null);
  const [clearStep, setClearStep] = useState(0); // 0 closed, 1 first confirm, 2 second confirm, 3 type-to-confirm
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState('');

  const closeClearDialog = () => {
    setClearStep(0);
    setClearConfirmText('');
  };

  const handleClearCalls = async () => {
    setClearing(true);
    setError('');
    setClearResult('');
    try {
      const res = await api.post('/system-config/clear-calls', { confirm: clearConfirmText.trim() });
      const d = res.data;
      setClearResult(`Cleared ${d.deleted_calls} call(s) and ${d.deleted_scheduled_calls} scheduled call(s); ${d.units_unassigned} unit(s) returned to Available. CAD numbering reset to 1.`);
      closeClearDialog();
    } catch {
      setError('Failed to clear calls. Confirmation phrase must be exactly DELETE.');
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    api.get('/system-config').then((r) => {
      setConfig(r.data);
      if (r.data.agency_logo) setLogoPreview(r.data.agency_logo);
    }).catch(() => setError('Failed to load'));
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/system-config/logo', formData);
      setLogoPreview(res.data.url);
      setConfig((c) => ({ ...c, agency_logo: res.data.url }));
      setSaved(false);
    } catch {
      setError('Failed to upload logo');
    }
  };

  const handleLogoDelete = async () => {
    try {
      await api.delete('/system-config/logo');
      setLogoPreview(null);
      setConfig((c) => ({ ...c, agency_logo: '' }));
    } catch {
      setError('Failed to remove logo');
    }
  };

  const updateMapView = useCallback(() => {
    if (!mapInstance.current) return;
    const lat = parseFloat(config.map_center_lat) || 39.8283;
    const lng = parseFloat(config.map_center_lng) || -98.5795;
    const zoom = parseInt(config.map_default_zoom) || 5;
    mapInstance.current.setView([lat, lng], zoom);
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
  }, [config.map_center_lat, config.map_center_lng, config.map_default_zoom]);

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      const lat = parseFloat(config.map_center_lat) || 39.8283;
      const lng = parseFloat(config.map_center_lng) || -98.5795;
      const zoom = parseInt(config.map_default_zoom) || 5;

      const map = L.map(mapRef.current).setView([lat, lng], zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      const marker = L.circleMarker([lat, lng], {
        radius: 10, fillColor: '#2196f3', color: '#fff', weight: 3, fillOpacity: 0.9,
      }).addTo(map).bindPopup('Map Center');
      markerRef.current = marker;
      mapInstance.current = map;
    }
  }, [config.map_center_lat, config.map_center_lng, config.map_default_zoom]);

  useEffect(() => {
    updateMapView();
  }, [updateMapView]);

  const handleUseCurrentView = () => {
    if (!mapInstance.current) return;
    const center = mapInstance.current.getCenter();
    const zoom = mapInstance.current.getZoom();
    setConfig((c) => ({
      ...c,
      map_center_lat: parseFloat(center.lat.toFixed(6)),
      map_center_lng: parseFloat(center.lng.toFixed(6)),
      map_default_zoom: zoom,
    }));
    if (markerRef.current) {
      markerRef.current.setLatLng(center);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((c) => ({ ...c, [field]: e.target.value }));
  };

  const handleSelectChange = (field: string) => (e: SelectChangeEvent<string>) => {
    setConfig((c) => ({ ...c, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaved(false);
    setError('');
    try {
      const res = await api.patch('/system-config', config);
      setConfig(res.data);
      setSaved(true);
    } catch {
      setError('Failed to save');
    }
  };

  const handleGenerateConnectorToken = async () => {
    try {
      const res = await api.post('/pbx-connector/generate-token');
      setConfig((c) => ({ ...c, pbx_connector_token: res.data.pbx_connector_token }));
      setSaved(true);
    } catch {
      setError('Failed to generate connector token');
    }
  };

  const handleGenerateAvlToken = async () => {
    try {
      const res = await api.post('/system-config/avl-token/generate');
      setConfig((c) => ({ ...c, avl_ingest_token: res.data.avl_ingest_token }));
      setSaved(true);
    } catch {
      setError('Failed to generate TAIP ingest key');
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>System Configuration</Typography>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Configuration saved</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Agency Logo</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload your agency logo. It will appear on the login page, PDF reports, and in the top status bar.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {logoPreview ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={logoPreview}
                alt="Agency Logo"
                sx={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', borderRadius: 1, bgcolor: 'background.default', p: 1 }}
              />
              <IconButton color="error" onClick={handleLogoDelete} size="small"><Delete /></IconButton>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No logo uploaded</Typography>
          )}
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif" hidden onChange={handleLogoUpload} />
          <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => fileInputRef.current?.click()}>
            {logoPreview ? 'Replace Logo' : 'Upload Logo'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Agency Name</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The agency name is used for branding on reports, PDF headers, and the login screen.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField fullWidth label="Agency Name" value={config.agency_name || ''} onChange={handleChange('agency_name')} placeholder="e.g., NECOMM Regional Communications" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Login Screen Disclaimer</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optional policy/warning shown on the dispatch and MDT login screens (e.g. "official use only, subject to logging"). Leave blank to hide.
        </Typography>
        <RichTextEditor
          value={config.login_disclaimer || ''}
          onChange={(html) => setConfig((c) => ({ ...c, login_disclaimer: html }))}
          placeholder="Enter your agency's login disclaimer / acceptable-use policy…"
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Jurisdiction</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The jurisdiction is used for geocoding and location search (biases results to this area).
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Jurisdiction Name" value={config.jurisdiction_name || ''} onChange={handleChange('jurisdiction_name')} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth label="State" value={config.jurisdiction_state || ''} onChange={handleChange('jurisdiction_state')} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth label="County" value={config.jurisdiction_county || ''} onChange={handleChange('jurisdiction_county')} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Default City (New Call)" value={config.default_city || ''} onChange={handleChange('default_city')} helperText="Pre-filled City on new calls; dispatchers can override" />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField fullWidth label="Default State" value={config.default_state || ''} onChange={handleChange('default_state')} helperText="Pre-filled State on new calls" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select value={config.timezone || 'UTC'} label="Timezone" onChange={handleSelectChange('timezone')}>
                {TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Map Defaults</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Center Latitude" type="number" value={config.map_center_lat ?? ''} onChange={handleChange('map_center_lat')} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Center Longitude" type="number" value={config.map_center_lng ?? ''} onChange={handleChange('map_center_lng')} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth label="Default Zoom" type="number" value={config.map_default_zoom ?? ''} onChange={handleChange('map_default_zoom')} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box
              ref={mapRef}
              sx={{
                height: 350,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mt: 1,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button variant="outlined" startIcon={<MyLocation />} onClick={handleUseCurrentView}>
                Use Current View
              </Button>
              <Typography variant="caption" color="text.secondary">
                Pan and zoom the map, then click "Use Current View" to save the position
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>CAD Number Format</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField fullWidth label="CAD Number Format"
              value={config.cad_number_format || ''}
              onChange={handleChange('cad_number_format')}
              helperText="Tokens: {YYYY} {YY} {MM} {DD} {SEQ} {SEQ3} {SEQ4} {SEQ5} {SEQ6}"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Example: CAD-{'{YYYY}{MM}{DD}'}-{'{SEQ4}'} → CAD-20260525-0001
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Session Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sessions renew automatically while the user is working, so a 10 or 12 hour shift never
          hits the login screen mid-shift. These limits are what end a session.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth label="Dispatch Idle Timeout (min)" type="number"
              value={config.dispatch_idle_timeout_minutes ?? 0}
              onChange={handleChange('dispatch_idle_timeout_minutes')}
              helperText="Sign out a dispatch console after this much inactivity. 0 = never (a console in a controlled room where a lockout mid-call is worse than a long session)."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth label="MDT Idle Timeout (min)" type="number"
              value={config.mdt_idle_timeout_minutes ?? 0}
              onChange={handleChange('mdt_idle_timeout_minutes')}
              helperText="Sign out an MDT after this much inactivity. 0 = never. Usually tighter than dispatch since a phone or laptop can leave the vehicle."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth label="Maximum Session Length (min)" type="number"
              value={config.session_max_minutes ?? 0}
              onChange={handleChange('session_max_minutes')}
              helperText="Force re-authentication this long after sign-in regardless of activity. 0 = no limit."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Dispatch Alerts</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth label="Pending Call Alert (seconds)" type="number"
              value={config.pending_call_alert_seconds ?? 60}
              onChange={handleChange('pending_call_alert_seconds')}
              helperText="Alert dispatchers when a call has no unit assigned for this many seconds. Set to 0 to disable."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SoundSelect label="Pending Call Alert Sound" value={config.sound_pending_call_alert || 'urgent'} onChange={handleSelectChange} field="sound_pending_call_alert" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VolumeUp color="primary" />
          <Typography variant="h6">Notification Sounds</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure alert sounds for dispatch and mobile screens. Click the play button to preview each sound.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Dispatcher Sounds</Typography>
            <SoundSelect label="New Call Created" value={config.sound_dispatch_new_call || 'tone_alert'} onChange={handleSelectChange} field="sound_dispatch_new_call" />
            <SoundSelect label="Unit Status Change" value={config.sound_dispatch_status_change || 'chime'} onChange={handleSelectChange} field="sound_dispatch_status_change" />
            <SoundSelect label="Unit Status Check" value={config.sound_dispatch_status_check || 'urgent'} onChange={handleSelectChange} field="sound_dispatch_status_check" />
            <SoundSelect label="Chat Message Received" value={config.sound_dispatch_chat || 'notification'} onChange={handleSelectChange} field="sound_dispatch_chat" />
            <SoundSelect label="MDT Emergency (DC) Alert" value={config.sound_dispatch_emergency || 'urgent'} onChange={handleSelectChange} field="sound_dispatch_emergency" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Mobile MDT Sounds</Typography>
            <SoundSelect label="Call Assigned" value={config.sound_mobile_call_assigned || 'dispatch_alert'} onChange={handleSelectChange} field="sound_mobile_call_assigned" />
            <SoundSelect label="Call Update" value={config.sound_mobile_call_update || 'notification'} onChange={handleSelectChange} field="sound_mobile_call_update" />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview All Sounds</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SOUND_OPTIONS.filter(s => s !== 'none').map((s) => (
              <Button key={s} size="small" variant="outlined" startIcon={<PlayArrow />} onClick={() => playSound(s)}>
                {s}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>TAIP over HTTP (AVL Gateways)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          TAIP reports POSTed to <strong>{window.location.origin}/api/units/taip</strong> are only accepted when they carry the
          ingest key below (X-AVL-Token header or ?key=) or come from an allowed source IP. With neither set, they are rejected.
          Traccar Client (/api/gps) and the TAIP TCP port are not affected.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 9 }}>
            <TextField fullWidth label="TAIP Ingest Key" value={config.avl_ingest_token ?? ''}
              onChange={handleChange('avl_ingest_token')}
              placeholder="Generate a shared secret for AVL gateways" />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button fullWidth variant="outlined" onClick={handleGenerateAvlToken}>Generate Key</Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth multiline minRows={2} label="Allowed Device IPs" value={config.avl_allowed_ips ?? ''}
              onChange={handleChange('avl_allowed_ips')}
              placeholder="e.g., 203.0.113.10, 198.51.100.0/24"
              helperText="IPs or CIDR ranges (comma or newline separated) allowed to POST TAIP reports without a key" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>PBX / Phone Integration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Auto-open the New Call form (pre-filled with caller ID + source) when a dispatcher answers an inbound
          phone call. Configure operator positions and their extensions in the Operator Positions admin page.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>PBX Integration</InputLabel>
              <Select value={config.pbx_enabled ? 'enabled' : 'disabled'} label="PBX Integration"
                onChange={(e) => setConfig({ ...config, pbx_enabled: e.target.value === 'enabled' })}
              >
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {config.pbx_enabled && (
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel>PBX Type</InputLabel>
                <Select value={config.pbx_type || 'freepbx'} label="PBX Type"
                  onChange={(e) => setConfig({ ...config, pbx_type: e.target.value })}
                >
                  <MenuItem value="freepbx">FreePBX / Asterisk (AMI)</MenuItem>
                  <MenuItem value="unifi">UniFi Talk (on-prem connector)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {config.pbx_enabled && (config.pbx_type || 'freepbx') === 'freepbx' && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="PBX Host" value={config.pbx_host ?? ''} onChange={handleChange('pbx_host')} placeholder="e.g., 192.168.1.100" />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="AMI Port" type="number" value={config.pbx_port ?? 5038} onChange={handleChange('pbx_port')} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="AMI Username" value={config.pbx_ami_user ?? ''} onChange={handleChange('pbx_ami_user')} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="AMI Password" type="password" value={config.pbx_ami_password ?? ''} onChange={handleChange('pbx_ami_password')} />
              </Grid>
            </>
          )}

          {config.pbx_enabled && config.pbx_type === 'unifi' && (
            <>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  UniFi Talk is not reachable from the cloud. Install the WatchCenter on-prem connector
                  (Windows service) on the customer LAN. It detects answered calls on UniFi Talk and pushes
                  them to the CAD using the token below. No inbound firewall/port-forwarding required.
                </Alert>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Connector Ingest URL" slotProps={{ input: { readOnly: true } }}
                  value={`${window.location.origin}/api/pbx-connector/call-event`}
                  helperText="Configure this URL in the on-prem connector agent." />
              </Grid>
              <Grid size={{ xs: 12, md: 9 }}>
                <TextField fullWidth label="Connector Token" value={config.pbx_connector_token ?? ''}
                  onChange={handleChange('pbx_connector_token')}
                  placeholder="Generate a shared secret for the on-prem connector" />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button fullWidth variant="outlined" onClick={handleGenerateConnectorToken}>Generate Token</Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Button variant="contained" size="large" onClick={handleSave}>Save Configuration</Button>

      <Paper sx={{ p: 3, mt: 4, mb: 3, border: '1px solid', borderColor: 'error.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmber color="error" />
          <Typography variant="h6" color="error">Danger Zone</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permanently delete <strong>all</strong> calls and scheduled calls and reset CAD numbering
          back to 1. Use this to clear test data before going live. Units, call types, statuses,
          locations, personnel, and all other configuration are <strong>not</strong> affected
          (assigned units are simply returned to Available). This cannot be undone.
        </Typography>
        {clearResult && <Alert severity="success" sx={{ mb: 2 }}>{clearResult}</Alert>}
        <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => { setClearResult(''); setClearStep(1); }}>
          Clear All Calls & Reset Numbering
        </Button>
      </Paper>

      {/* Step 1: first confirmation */}
      <Dialog open={clearStep === 1} onClose={closeClearDialog}>
        <DialogTitle>Delete all calls?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all calls and reset CAD numbering to 1?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearDialog}>No</Button>
          <Button color="error" onClick={() => setClearStep(2)}>Yes</Button>
        </DialogActions>
      </Dialog>

      {/* Step 2: second confirmation */}
      <Dialog open={clearStep === 2} onClose={closeClearDialog}>
        <DialogTitle>Are you really, really sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes every call and scheduled call for this tenant and resets
            numbering to 1. <strong>There is no going back.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearDialog}>No</Button>
          <Button color="error" onClick={() => setClearStep(3)}>Yes, continue</Button>
        </DialogActions>
      </Dialog>

      {/* Step 3: type-to-confirm */}
      <Dialog open={clearStep === 3} onClose={closeClearDialog}>
        <DialogTitle>Final confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm, type <strong>DELETE</strong> in the box below, then click Delete All Calls.
          </DialogContentText>
          <TextField
            autoFocus fullWidth label='Type "DELETE" to confirm'
            value={clearConfirmText}
            onChange={(e) => setClearConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearDialog}>Cancel</Button>
          <Button
            color="error" variant="contained"
            disabled={clearConfirmText.trim().toUpperCase() !== 'DELETE' || clearing}
            onClick={handleClearCalls}
          >
            {clearing ? 'Deleting…' : 'Delete All Calls'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
